import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
} from "@/components/ui/combobox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InputGroupAddon } from "@/components/ui/input-group";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { getChampionImageUrl, type Champion } from "@/state/league";
import { addMonths, endOfDay, format, startOfDay } from "date-fns";
import { ChevronDownIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const LANE_ORDER = ["top", "jungle", "middle", "bottom", "support"] as const;

type LaneValue = (typeof LANE_ORDER)[number];

type LaneConfig = {
  label: string;
  blueKey: keyof ProplayGame;
  redKey: keyof ProplayGame;
};

type ProplayGame = {
  game_id: string;
  game_date?: string;
  game_name?: string;
  tournament?: string;
  bluetop_name?: string;
  bluejgl_name?: string;
  bluemid_name?: string;
  bluebot_name?: string;
  bluesup_name?: string;
  redtop_name?: string;
  redjgl_name?: string;
  redmid_name?: string;
  redbot_name?: string;
  redsup_name?: string;
};

type SelectItem = {
  label: string;
  value: string;
};

type ChampionStat = {
  name: string;
  count: number;
  ids: string[];
  score: number;
};

const LANE_CONFIG: Record<LaneValue, LaneConfig> = {
  top: { label: "Top", blueKey: "bluetop_name", redKey: "redtop_name" },
  jungle: { label: "Jungle", blueKey: "bluejgl_name", redKey: "redjgl_name" },
  middle: { label: "Mid", blueKey: "bluemid_name", redKey: "redmid_name" },
  bottom: { label: "Bottom", blueKey: "bluebot_name", redKey: "redbot_name" },
  support: { label: "Support", blueKey: "bluesup_name", redKey: "redsup_name" },
};

const createEmptySelections = (): Record<LaneValue, string> => ({
  top: "",
  jungle: "",
  middle: "",
  bottom: "",
  support: "",
});

const createDefaultFromDate = (): Date => addMonths(new Date(), -2);

const createDefaultToDate = (): Date => new Date();

const parseGameDate = (value?: string): Date | null => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

export function ProplaySynergy() {
  const [selected, setSelected] = useState<Record<LaneValue, string>>(createEmptySelections);
  const [games, setGames] = useState<ProplayGame[]>([]);
  const [dateFrom, setDateFrom] = useState<Date | undefined>(() => createDefaultFromDate());
  const [dateTo, setDateTo] = useState<Date | undefined>(() => createDefaultToDate());
  const [championIcons, setChampionIcons] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLane, setDialogLane] = useState<LaneValue | null>(null);
  const [dialogChampion, setDialogChampion] = useState("");
  const [dialogIds, setDialogIds] = useState<string[]>([]);
  const [matchResults, setMatchResults] = useState<Record<string, "blue" | "red" | null>>({});
  const [matchLoading, setMatchLoading] = useState(false);

  const loadGames = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/proplay/games");
      if (!res.ok) {
        const message = await res.text();
        throw new Error(message || "Failed to load games");
      }

      const data = await res.json();
      if (!Array.isArray(data)) {
        throw new Error("Unexpected response from server");
      }

      setGames(data as ProplayGame[]);
    } catch (err) {
      setGames([]);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadGames();
  }, []);

  useEffect(() => {
    let isActive = true;

    const loadChampionIcons = async () => {
      try {
        const versionsRes = await fetch("https://ddragon.leagueoflegends.com/api/versions.json", {
          cache: "no-store",
        });
        if (!versionsRes.ok) return;

        const versions = (await versionsRes.json()) as string[];
        const version = Array.isArray(versions) ? versions[0] : undefined;
        if (!version) return;

        const champRes = await fetch(
          `https://ddragon.leagueoflegends.com/cdn/${version}/data/en_US/champion.json`,
          { cache: "no-store" }
        );
        if (!champRes.ok) return;

        const json = (await champRes.json()) as { data?: Record<string, Champion> };
        const list = Object.values(json.data ?? {});
        const nextIcons: Record<string, string> = {};
        for (const champ of list) {
          nextIcons[champ.id.toLowerCase()] = getChampionImageUrl(champ, version);
          nextIcons[champ.name.toLowerCase()] = getChampionImageUrl(champ, version);
        }

        if (isActive) {
          setChampionIcons(nextIcons);
        }
      } catch (err) {
        console.warn("Failed to load champion icons:", err);
      }
    };

    void loadChampionIcons();

    return () => {
      isActive = false;
    };
  }, []);

  const filteredGames = useMemo(() => {
    if (!dateFrom && !dateTo) return games;

    const from = dateFrom ? startOfDay(dateFrom) : null;
    const to = dateTo ? endOfDay(dateTo) : null;

    return games.filter(game => {
      const date = parseGameDate(game.game_date);
      if (!date) return false;
      if (from && date < from) return false;
      if (to && date > to) return false;
      return true;
    });
  }, [dateFrom, dateTo, games]);

  const fromLabel = useMemo(() => {
    if (!dateFrom) return "From date";
    return format(dateFrom, "PPP");
  }, [dateFrom]);

  const toLabel = useMemo(() => {
    if (!dateTo) return "To date";
    return format(dateTo, "PPP");
  }, [dateTo]);

  const championItemsByLane = useMemo(() => {
    const result: Record<LaneValue, SelectItem[]> = {
      top: [],
      jungle: [],
      middle: [],
      bottom: [],
      support: [],
    };

    for (const lane of LANE_ORDER) {
      const config = LANE_CONFIG[lane];
      const names = new Set<string>();

      for (const game of filteredGames) {
        const blueName = game[config.blueKey];
        const redName = game[config.redKey];
        if (blueName) names.add(blueName);
        if (redName) names.add(redName);
      }

      const items = Array.from(names)
        .sort((a, b) => a.localeCompare(b))
        .map(name => ({ label: name, value: name }));

      result[lane] = [{ label: "Any", value: "" }, ...items];
    }

    return result;
  }, [filteredGames]);

  useEffect(() => {
    setSelected(prev => {
      const next = { ...prev };
      let changed = false;

      for (const lane of LANE_ORDER) {
        const value = prev[lane];
        if (!value) continue;
        const items = championItemsByLane[lane];
        if (!items?.some(item => item.value === value)) {
          next[lane] = "";
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [championItemsByLane]);

  const hasFilters = LANE_ORDER.some(lane => Boolean(selected[lane]));

  const baseStats = useMemo(() => {
    const counts: Record<LaneValue, Map<string, number>> = {
      top: new Map(),
      jungle: new Map(),
      middle: new Map(),
      bottom: new Map(),
      support: new Map(),
    };
    let totalSides = 0;

    for (const game of filteredGames) {
      for (const side of ["blue", "red"] as const) {
        totalSides += 1;
        for (const lane of LANE_ORDER) {
          const config = LANE_CONFIG[lane];
          const champName = side === "blue" ? game[config.blueKey] : game[config.redKey];
          if (!champName) continue;

          const laneMap = counts[lane];
          laneMap.set(champName, (laneMap.get(champName) ?? 0) + 1);
        }
      }
    }

    return { counts, totalSides };
  }, [filteredGames]);

  const gamesById = useMemo(() => new Map(games.map(game => [game.game_id, game])), [games]);

  const loadMatchResults = async (ids: string[]) => {
    // ponytail: retry ids that came back null (transient scrape failures) instead of caching them forever
    const pending = ids.filter(id => !matchResults[id]);
    if (pending.length === 0) return;

    setMatchLoading(true);
    try {
      const res = await fetch("/api/proplay/results", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: pending }),
      });
      if (!res.ok) return;

      const data = (await res.json()) as { results?: Record<string, "blue" | "red" | null> };
      if (data.results) {
        setMatchResults(prev => ({ ...prev, ...data.results }));
      }
    } catch (err) {
      console.warn("Failed to load match results:", err);
    } finally {
      setMatchLoading(false);
    }
  };

  const getMatchDisplay = (id: string) => {
    const game = gamesById.get(id);
    const config = dialogLane ? LANE_CONFIG[dialogLane] : null;
    let opponent: string | undefined;
    let resultLabel: "WIN" | "LOSE" | null = null;

    if (game && config) {
      const blueName = game[config.blueKey];
      const redName = game[config.redKey];
      const side = blueName === dialogChampion ? "blue" : redName === dialogChampion ? "red" : null;
      opponent = side === "blue" ? redName : side === "red" ? blueName : undefined;

      const winnerSide = matchResults[id];
      if (side && winnerSide) {
        resultLabel = winnerSide === side ? "WIN" : "LOSE";
      }
    }

    return { game, opponent, resultLabel };
  };

  const openChampionGames = (lane: LaneValue, item: ChampionStat) => {
    if (item.ids.length === 0) return;
    const ids = [...item.ids].sort((a, b) => Number(b) - Number(a));

    if (ids.length === 1) {
      window.open(`https://gol.gg/game/stats/${ids[0]}/page-game/`, "_blank", "noopener,noreferrer");
      return;
    }

    setDialogLane(lane);
    setDialogChampion(item.name);
    setDialogIds(ids);
    setDialogOpen(true);
    void loadMatchResults(ids);
  };

  const results = useMemo(() => {
    if (!hasFilters || filteredGames.length === 0) return null;

    const counts: Record<LaneValue, Map<string, number>> = {
      top: new Map(),
      jungle: new Map(),
      middle: new Map(),
      bottom: new Map(),
      support: new Map(),
    };
    const idsByLane: Record<LaneValue, Map<string, Set<string>>> = {
      top: new Map(),
      jungle: new Map(),
      middle: new Map(),
      bottom: new Map(),
      support: new Map(),
    };

    let matchedTeams = 0;

    const matchesSide = (game: ProplayGame, side: "blue" | "red") => {
      for (const lane of LANE_ORDER) {
        const desired = selected[lane];
        if (!desired) continue;
        const config = LANE_CONFIG[lane];
        const champName = side === "blue" ? game[config.blueKey] : game[config.redKey];
        if (champName !== desired) return false;
      }

      return true;
    };

    for (const game of filteredGames) {
      for (const side of ["blue", "red"] as const) {
        if (!matchesSide(game, side)) continue;

        matchedTeams += 1;
        for (const lane of LANE_ORDER) {
          const config = LANE_CONFIG[lane];
          const champName = side === "blue" ? game[config.blueKey] : game[config.redKey];
          if (!champName) continue;

          const laneMap = counts[lane];
          laneMap.set(champName, (laneMap.get(champName) ?? 0) + 1);

          if (game.game_id) {
            const idMap = idsByLane[lane];
            const idSet = idMap.get(champName) ?? new Set<string>();
            idSet.add(game.game_id);
            idMap.set(champName, idSet);
          }
        }
      }
    }

    const byLane = LANE_ORDER.map(lane => {
      const selectedChampion = selected[lane];
      let items: ChampionStat[] = Array.from(counts[lane].entries()).map(([name, count]) => {
        const baseCount = baseStats.counts[lane].get(name) ?? 0;
        const score =
          matchedTeams > 0 && baseCount > 0 && baseStats.totalSides > 0
            ? (count / matchedTeams) / (baseCount / baseStats.totalSides)
            : 0;

        return {
          name,
          count,
          ids: Array.from(idsByLane[lane].get(name) ?? []),
          score,
        };
      });

      items.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

      if (selectedChampion) {
        const count = counts[lane].get(selectedChampion) ?? 0;
        const ids = Array.from(idsByLane[lane].get(selectedChampion) ?? []);
        const baseCount = baseStats.counts[lane].get(selectedChampion) ?? 0;
        const score =
          matchedTeams > 0 && baseCount > 0 && baseStats.totalSides > 0
            ? (count / matchedTeams) / (baseCount / baseStats.totalSides)
            : 0;
        items = [{ name: selectedChampion, count, ids, score }];
      }

      return {
        lane,
        items,
        selectedChampion,
      };
    });

    return { matchedTeams, byLane };
  }, [baseStats, filteredGames, hasFilters, selected]);

  const showEmpty = !loading && games.length === 0 && !error;
  const showRangeEmpty = !loading && games.length > 0 && filteredGames.length === 0 && !error;

  return (
    <div className="flex flex-col gap-4 w-[1000px] max-w-full">
      <div className="flex flex-wrap items-center gap-3">
        <Popover>
          <PopoverTrigger
            render={
              <Button
                variant="outline"
                data-empty={!dateFrom}
                className="w-[220px] justify-between text-left font-normal data-[empty=true]:text-muted-foreground"
              >
                {fromLabel}
                <ChevronDownIcon className="ml-2 size-4 shrink-0 text-muted-foreground" />
              </Button>
            }
          />
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              numberOfMonths={1}
              selected={dateFrom}
              onSelect={setDateFrom}
              defaultMonth={dateFrom ?? addMonths(new Date(), -2)}
            />
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger
            render={
              <Button
                variant="outline"
                data-empty={!dateTo}
                className="w-[220px] justify-between text-left font-normal data-[empty=true]:text-muted-foreground"
              >
                {toLabel}
                <ChevronDownIcon className="ml-2 size-4 shrink-0 text-muted-foreground" />
              </Button>
            }
          />
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              numberOfMonths={1}
              selected={dateTo}
              onSelect={setDateTo}
              defaultMonth={dateTo ?? new Date()}
            />
          </PopoverContent>
        </Popover>
        <div className="text-sm text-muted-foreground">
          Games in range: <span className="font-medium text-foreground">{filteredGames.length}</span>
          {games.length > 0 ? ` / ${games.length}` : ""}
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {LANE_ORDER.map(lane => {
          const config = LANE_CONFIG[lane];
          const items = championItemsByLane[lane] ?? [{ label: "Any", value: "" }];
          const itemValues = items.map(item => item.value);
          const itemLabels = new Map(items.map(item => [item.value, item.label]));
          const selectedValue = selected[lane];
          const selectedIconUrl = selectedValue ? championIcons[selectedValue.toLowerCase()] : undefined;

          return (
            <div key={lane} className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">{config.label}</span>
              <Combobox
                value={selectedValue}
                onValueChange={value =>
                  setSelected(prev => ({
                    ...prev,
                    [lane]: (value as string) ?? "",
                  }))
                }
                items={itemValues}
                disabled={items.length <= 1}
              >
                <ComboboxInput
                  placeholder={`Any ${config.label}`}
                  className="w-full"
                  showClear={Boolean(selectedValue)}
                >
                  {selectedIconUrl ? (
                    <InputGroupAddon align="inline-start" className="px-1.5">
                      <img src={selectedIconUrl} alt="" className="size-5 rounded-sm" loading="lazy" />
                    </InputGroupAddon>
                  ) : null}
                </ComboboxInput>
                <ComboboxContent>
                  <ComboboxEmpty>No champions found.</ComboboxEmpty>
                  <ComboboxList>
                    {(value: string) => {
                      const label = itemLabels.get(value) ?? value;
                      const iconUrl = value ? championIcons[label.toLowerCase()] : undefined;

                      return (
                        <ComboboxItem key={`${lane}-${value || "any"}`} value={value}>
                          {iconUrl ? (
                            <img
                              src={iconUrl}
                              alt=""
                              className="size-5 rounded-sm"
                              loading="lazy"
                            />
                          ) : (
                            <span className="size-5" />
                          )}
                          <span className="truncate">{label}</span>
                        </ComboboxItem>
                      );
                    }}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <Button type="button" variant="secondary" size="sm" onClick={loadGames} disabled={loading}>
          {loading ? "Loading..." : "Reload data"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setSelected(createEmptySelections())}
          disabled={!hasFilters}
        >
          Clear filters
        </Button>
        {loading ? <span>Loading games...</span> : null}
        {error ? <span className="text-destructive">{error}</span> : null}
        {showEmpty ? <span>No stored games yet. Run the fetcher first.</span> : null}
        {showRangeEmpty ? <span>No games in the selected date range.</span> : null}
      </div>

      {!hasFilters && !showEmpty && !showRangeEmpty && !loading ? (
        <div className="text-sm text-muted-foreground">Select at least one champion to see synergies.</div>
      ) : null}

      {results ? (
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Matched teams: <span className="font-medium text-foreground">{results.matchedTeams}</span>
          </div>
          <div className="grid gap-4 md:grid-cols-5">
            {results.byLane.map(role => (
              <div key={role.lane} className="rounded-md border border-border/70 p-3">
                <div className="flex items-center justify-between text-sm font-semibold">
                  <span>{LANE_CONFIG[role.lane].label}</span>
                  {role.selectedChampion ? (
                    <span className="text-xs font-normal text-muted-foreground">Locked</span>
                  ) : null}
                </div>
                {role.items.length === 0 ? (
                  <div className="mt-2 text-sm text-muted-foreground">No matches yet.</div>
                ) : (
                  <ul className="mt-2 space-y-1 text-sm">
                    {role.items.slice(0, 8).map(item => {
                      const iconUrl = championIcons[item.name.toLowerCase()];

                      return (
                        <li key={item.name} className="flex items-center justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => openChampionGames(role.lane, item)}
                            disabled={item.ids.length === 0}
                            className="flex min-w-0 items-center gap-2 text-left hover:underline disabled:cursor-default disabled:text-muted-foreground"
                          >
                            {iconUrl ? (
                              <img
                                src={iconUrl}
                                alt=""
                                className="size-5 rounded-sm"
                                loading="lazy"
                              />
                            ) : (
                              <span className="size-5" />
                            )}
                            <span className="truncate">{item.name}</span>
                          </button>
                        <span className="flex flex-col items-end text-muted-foreground">
                          <span>{item.count}</span>
                          <span className="text-xs">Score {item.score.toFixed(2)}x</span>
                        </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialogChampion}
              {dialogLane ? ` - ${LANE_CONFIG[dialogLane].label}` : ""}
            </DialogTitle>
            <DialogDescription>Found {dialogIds.length} games. Click to open on gol.gg.</DialogDescription>
          </DialogHeader>
          {dialogIds.length === 0 ? (
            <div className="text-sm text-muted-foreground">No games found.</div>
          ) : (
            <ul className="max-h-[320px] space-y-2 overflow-y-auto text-sm">
              {dialogIds.map(id => {
                const { game, opponent, resultLabel } = getMatchDisplay(id);

                return (
                  <li key={id}>
                    <a
                      href={`https://gol.gg/game/stats/${id}/page-game/`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline"
                    >
                      <span
                        className={
                          resultLabel === "WIN"
                            ? "font-semibold text-emerald-500"
                            : resultLabel === "LOSE"
                              ? "font-semibold text-red-500"
                              : "text-muted-foreground"
                        }
                      >
                        {resultLabel ?? (matchLoading ? "…" : "?")}
                      </span>{" "}
                      vs {opponent ?? "Unknown"} · {game?.game_name ?? `Game ${id}`}
                      {game?.tournament ? ` · ${game.tournament}` : ""}
                    </a>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
